import {
  allEthnicities,
  ETHNICITY_REGIONS,
  ethnicityRegion
} from 'src/components/questionnaire/Constants';

export function mergeEthnicityRows(custom) {
  const overrides = new Map(
    (Array.isArray(custom) ? custom : []).map((e) => [e.name, e])
  );

  return allEthnicities.map((name) => {
    const override = overrides.get(name);
    return {
      name,
      show: override?.show ?? true,
      topOfList: override?.topOfList ?? false,
      region: ethnicityRegion(name)
    };
  });
}

const byName = (a, b) => a.localeCompare(b);

/**
 * Every ASCCEG name, A-Z. `allEthnicities` itself is in ASCCEG publication
 * order (grouped by region), which reads as no order at all in a dropdown, so
 * anything user-facing wants this list and not that one. `allEthnicities` keeps
 * its region order for the Settings table, which groups by region.
 */
export const allEthnicitiesAlphabetical = [...allEthnicities].sort(byName);

/**
 * The option list for an ethnicity picker: alphabetical, with any entry pinned
 * to the top of the list (Settings -> Pedigree -> Ethnicity list) first and
 * alphabetical within that pinned section. Hidden entries are dropped.
 *
 * Sorts unconditionally (TGCLOUD-1733) - a tenant with no saved customisation
 * used to get the raw ASCCEG order back.
 */
export function resolveEthnicityOptions(custom) {
  if (!Array.isArray(custom) || custom.length === 0)
    return allEthnicitiesAlphabetical;

  const visible = mergeEthnicityRows(custom).filter((e) => e.show);
  const names = (pinned) =>
    visible
      .filter((e) => !!e.topOfList === pinned)
      .map((e) => e.name)
      .sort(byName);

  return [...names(true), ...names(false)];
}

export function readEthnicitySettings(responseData) {
  if (Array.isArray(responseData)) return responseData; // defensive
  return responseData?.ethnicity || [];
}

export function deriveEthnicityBulkState(inScope) {
  const rows = Array.isArray(inScope) ? inScope : [];
  const total = rows.length;
  const shown = rows.filter((e) => e.show).length;
  const hidden = total - shown;
  const pinned = rows.filter((e) => e.topOfList).length;

  return {
    total,
    shown,
    hidden,
    pinned,
    visibility:
      total > 0 && hidden === 0
        ? { action: 'hide', patch: { show: false }, count: total }
        : { action: 'show', patch: { show: true }, count: hidden },
    pin:
      pinned === 0
        ? { action: 'pin', patch: { topOfList: true }, count: total }
        : { action: 'unpin', patch: { topOfList: false }, count: pinned }
  };
}

export { ETHNICITY_REGIONS, ethnicityRegion, allEthnicities };
